import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, LOGO_FALLBACK } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Save, Upload, Trash2, Plus, RefreshCw, ExternalLink } from "lucide-react";

const TABS = [
  { id: "hero", label: "Hero" },
  { id: "contact", label: "Contacto" },
  { id: "services", label: "Servicios" },
  { id: "pricing", label: "Precios" },
  { id: "manifesto", label: "Manifiesto" },
  { id: "testimonials", label: "Testimonios" },
  { id: "gallery", label: "Galería" },
  { id: "before_after", label: "Antes/Después" },
  { id: "faq", label: "FAQ" },
  { id: "marquee", label: "Marquee" },
  { id: "contacts", label: "Solicitudes" },
];

export default function AdminDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const nav = useNavigate();
  const [site, setSite] = useState(null);
  const [tab, setTab] = useState("hero");
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    if (!authLoading && !user) nav("/admin/login", { replace: true });
  }, [authLoading, user, nav]);

  const load = async () => {
    const { data } = await api.get("/site");
    setSite(data);
  };
  const loadContacts = async () => {
    try {
      const { data } = await api.get("/admin/contacts");
      setContacts(data || []);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { if (user) { load(); loadContacts(); } }, [user]);

  if (authLoading || !user) return null;
  if (!site) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--gcs-ivory)]">
        <RefreshCw className="w-6 h-6 animate-spin text-[color:var(--gcs-emerald)]" />
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site", site);
      toast.success("Cambios guardados.");
    } catch (e) {
      toast.error("Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const patchSite = (patch) => setSite((s) => ({ ...s, ...patch }));

  return (
    <div className="min-h-screen bg-[color:var(--gcs-ivory)]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[color:var(--gcs-emerald)] text-[color:var(--gcs-ivory)] border-b border-[color:var(--gcs-line-dark)]">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={site.hero?.logo_url || LOGO_FALLBACK} alt="Logo" className="h-10 w-10 object-contain bg-[color:var(--gcs-ivory)] rounded-sm p-1" />
            <div>
              <div className="font-serif text-lg">Panel · General Cleaning Services</div>
              <div className="overline text-[color:var(--gcs-gold)]">Editando contenido en vivo</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noreferrer" className="rounded-full px-4 py-2 text-xs border border-[color:var(--gcs-ivory)]/40 hover:bg-[color:var(--gcs-ivory)] hover:text-[color:var(--gcs-emerald)] transition-colors inline-flex items-center gap-2" data-testid="admin-view-site">
              Ver sitio <ExternalLink className="w-3 h-3" />
            </a>
            <button
              onClick={save}
              disabled={saving}
              className="rounded-full btn-gold px-5 py-2 text-xs font-semibold inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="admin-save-btn"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
            <button
              onClick={async () => { await logout(); nav("/admin/login"); }}
              className="rounded-full px-3 py-2 text-xs border border-[color:var(--gcs-ivory)]/40 hover:bg-[color:var(--gcs-ivory)] hover:text-[color:var(--gcs-emerald)] transition-colors inline-flex items-center gap-2"
              data-testid="admin-logout"
            >
              <LogOut className="w-3.5 h-3.5" /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-8 grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Sidebar tabs */}
        <aside className="md:col-span-3">
          <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => t.id === "contacts" ? (setTab(t.id), loadContacts()) : setTab(t.id)}
                className={`text-left px-4 py-3 text-sm rounded-sm border transition-colors ${
                  tab === t.id
                    ? "bg-[color:var(--gcs-emerald)] text-[color:var(--gcs-ivory)] border-[color:var(--gcs-emerald)]"
                    : "bg-[color:var(--gcs-ivory-2)] text-[color:var(--gcs-emerald)] border-[color:var(--gcs-line)] hover:bg-[color:var(--gcs-ivory-3)]"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Panels */}
        <section className="md:col-span-9 bg-[color:var(--gcs-ivory-2)] border border-[color:var(--gcs-line)] rounded-sm p-6 md:p-8">
          {tab === "hero" && (
            <HeroEditor
              hero={site.hero}
              onChange={(hero) => patchSite({ hero })}
              uploadImage={uploadImage}
            />
          )}
          {tab === "contact" && (
            <ContactEditor
              contact={site.contact}
              onChange={(contact) => patchSite({ contact })}
            />
          )}
          {tab === "services" && (
            <ListEditor
              title="Services / Servicios"
              items={site.services}
              onChange={(services) => patchSite({ services })}
              schema={[
                { key: "name", label: "Name", l10n: true },
                { key: "price_from", label: "Price from", l10n: true },
                { key: "description", label: "Description", l10n: true, textarea: true },
                { key: "features", label: "Features (one per line)", l10nArray: true },
                { key: "image_url", label: "Image (URL)", uploader: uploadImage },
              ]}
              emptyItem={{ name: { en: "New service", es: "Nuevo servicio" }, price_from: { en: "From $0", es: "Desde $0" }, description: { en: "", es: "" }, features: [], image_url: "" }}
              testid="services"
            />
          )}
          {tab === "pricing" && (
            <ListEditor
              title="Pricing plans / Planes de precios"
              items={site.pricing}
              onChange={(pricing) => patchSite({ pricing })}
              schema={[
                { key: "name", label: "Name", l10n: true },
                { key: "price", label: "Price" },
                { key: "subtitle", label: "Subtitle", l10n: true },
                { key: "features", label: "Features (one per line)", l10nArray: true },
                { key: "highlighted", label: "Highlighted", boolean: true },
              ]}
              emptyItem={{ name: { en: "New plan", es: "Nuevo plan" }, price: "$0", subtitle: { en: "", es: "" }, features: [], highlighted: false }}
              testid="pricing"
            />
          )}
          {tab === "manifesto" && (
            <ListEditor
              title="Manifesto (chapters)"
              items={site.manifesto}
              onChange={(manifesto) => patchSite({ manifesto })}
              schema={[
                { key: "number", label: "Number" },
                { key: "title", label: "Title", l10n: true },
                { key: "body", label: "Body", l10n: true, textarea: true },
              ]}
              emptyItem={{ number: "07", title: { en: "New chapter", es: "Nuevo capítulo" }, body: { en: "", es: "" } }}
              testid="manifesto"
            />
          )}
          {tab === "testimonials" && (
            <ListEditor
              title="Testimonials / Testimonios"
              items={site.testimonials}
              onChange={(testimonials) => patchSite({ testimonials })}
              schema={[
                { key: "quote", label: "Quote", l10n: true, textarea: true },
                { key: "author", label: "Author" },
                { key: "location", label: "Location" },
              ]}
              emptyItem={{ quote: { en: "", es: "" }, author: "", location: "" }}
              testid="testimonials"
            />
          )}
          {tab === "gallery" && (
            <ListEditor
              title="Gallery / Galería"
              items={site.gallery}
              onChange={(gallery) => patchSite({ gallery })}
              schema={[
                { key: "url", label: "Image (URL)", uploader: uploadImage },
                { key: "caption", label: "Caption", l10n: true },
              ]}
              emptyItem={{ url: "", caption: { en: "", es: "" } }}
              testid="gallery"
            />
          )}
          {tab === "before_after" && (
            <ListEditor
              title="Before / After · Antes / Después"
              items={site.before_after || []}
              onChange={(before_after) => patchSite({ before_after })}
              schema={[
                { key: "before_url", label: "Image BEFORE", uploader: uploadImage },
                { key: "after_url", label: "Image AFTER", uploader: uploadImage },
                { key: "caption", label: "Caption (e.g. 'Kitchen · Rockville, MD')", l10n: true },
              ]}
              emptyItem={{ before_url: "", after_url: "", caption: { en: "", es: "" } }}
              testid="before-after"
            />
          )}
          {tab === "faq" && (
            <ListEditor
              title="FAQ · Preguntas frecuentes"
              items={site.faq}
              onChange={(faq) => patchSite({ faq })}
              schema={[
                { key: "question", label: "Question", l10n: true },
                { key: "answer", label: "Answer", l10n: true, textarea: true },
              ]}
              emptyItem={{ question: { en: "", es: "" }, answer: { en: "", es: "" } }}
              testid="faq"
            />
          )}
          {tab === "marquee" && (
            <MarqueeEditor
              words={site.marquee_words || []}
              onChange={(marquee_words) => patchSite({ marquee_words })}
            />
          )}
          {tab === "contacts" && (
            <ContactsList contacts={contacts} onReload={loadContacts} />
          )}
        </section>
      </div>
    </div>
  );
}

/* ----- Sub-editors ----- */

function Field({ label, children }) {
  return (
    <label className="block mb-4">
      <span className="overline text-[color:var(--gcs-emerald)]/70">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function TextInput({ value, onChange, placeholder, testid }) {
  return (
    <input
      className="w-full px-3 py-2 bg-[color:var(--gcs-ivory)] border border-[color:var(--gcs-line)] rounded-sm text-sm"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
    />
  );
}
function TextArea({ value, onChange, testid }) {
  return (
    <textarea
      className="w-full px-3 py-2 bg-[color:var(--gcs-ivory)] border border-[color:var(--gcs-line)] rounded-sm text-sm min-h-[100px]"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    />
  );
}
function ImageField({ url, onChange, uploader, testid }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const u = await uploader(file);
      onChange(u);
      toast.success("Imagen subida.");
    } catch { toast.error("Error al subir."); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-start gap-4">
      <div className="w-24 h-24 bg-[color:var(--gcs-ivory)] border border-[color:var(--gcs-line)] rounded-sm overflow-hidden flex items-center justify-center">
        {url ? <img src={url} alt="preview" className="w-full h-full object-cover" /> : <span className="text-xs text-[color:var(--gcs-emerald)]/50">Sin imagen</span>}
      </div>
      <div className="flex-1">
        <input
          className="w-full px-3 py-2 bg-[color:var(--gcs-ivory)] border border-[color:var(--gcs-line)] rounded-sm text-xs mb-2"
          value={url || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Pega una URL o sube archivo"
          data-testid={`${testid}-url`}
        />
        <label className="inline-flex items-center gap-2 rounded-full btn-outline-emerald px-4 py-1.5 text-xs font-semibold cursor-pointer">
          <Upload className="w-3 h-3" />
          {busy ? "Subiendo..." : "Subir archivo"}
          <input type="file" accept="image/*" className="hidden" onChange={handle} data-testid={`${testid}-file`} />
        </label>
      </div>
    </div>
  );
}

function HeroEditor({ hero, onChange, uploadImage }) {
  const set = (k, v) => onChange({ ...hero, [k]: v });
  return (
    <div>
      <h2 className="font-serif text-2xl mb-6 text-[color:var(--gcs-emerald)]">Hero</h2>
      <Field label="Eyebrow / Etiqueta superior"><L10nInput value={hero.eyebrow} onChange={(v) => set("eyebrow", v)} testid="hero-eyebrow" /></Field>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Line 1 / Línea 1"><L10nInput value={hero.line_1} onChange={(v) => set("line_1", v)} testid="hero-line1" /></Field>
        <Field label="Line 2 (gold) / Línea 2 (dorado)"><L10nInput value={hero.line_2} onChange={(v) => set("line_2", v)} testid="hero-line2" /></Field>
        <Field label="Line 3 / Línea 3"><L10nInput value={hero.line_3} onChange={(v) => set("line_3", v)} testid="hero-line3" /></Field>
      </div>
      <Field label="Subtitle / Subtítulo"><L10nInput value={hero.subtitle} onChange={(v) => set("subtitle", v)} textarea testid="hero-subtitle" /></Field>
      <Field label="Background image / Imagen de fondo">
        <ImageField url={hero.background_image} onChange={(v) => set("background_image", v)} uploader={uploadImage} testid="hero-bg" />
      </Field>
      <Field label="Logo (shown dynamically in the hero)">
        <ImageField url={hero.logo_url} onChange={(v) => set("logo_url", v)} uploader={uploadImage} testid="hero-logo" />
      </Field>
    </div>
  );
}

function ContactEditor({ contact, onChange }) {
  const set = (k, v) => onChange({ ...contact, [k]: v });
  return (
    <div>
      <h2 className="font-serif text-2xl mb-6 text-[color:var(--gcs-emerald)]">Contact / Contacto</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Business name"><TextInput value={contact.business_name} onChange={(v) => set("business_name", v)} testid="contact-business-input" /></Field>
        <Field label="Owner"><TextInput value={contact.owner} onChange={(v) => set("owner", v)} testid="contact-owner-input" /></Field>
        <Field label="Phone / Teléfono"><TextInput value={contact.phone} onChange={(v) => set("phone", v)} testid="contact-phone-input" /></Field>
        <Field label="Email"><TextInput value={contact.email} onChange={(v) => set("email", v)} testid="contact-email-input" /></Field>
        <Field label="WhatsApp (digits only)"><TextInput value={contact.whatsapp} onChange={(v) => set("whatsapp", v)} testid="contact-whatsapp-input" /></Field>
      </div>
      <Field label="Service area / Área de servicio"><L10nInput value={contact.service_area} onChange={(v) => set("service_area", v)} testid="contact-area" /></Field>
      <Field label="Tagline (footer)"><L10nInput value={contact.tagline} onChange={(v) => set("tagline", v)} textarea testid="contact-tagline" /></Field>
    </div>
  );
}

function MarqueeEditor({ words, onChange }) {
  const set = (i, key, val) => {
    const next = [...words];
    const cur = toL10n(words[i]);
    next[i] = { ...cur, [key]: val };
    onChange(next);
  };
  const add = () => onChange([...words, { en: "", es: "" }]);
  const remove = (i) => onChange(words.filter((_, k) => k !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-[color:var(--gcs-emerald)]">Marquee words</h2>
        <button onClick={add} className="rounded-full btn-outline-emerald px-4 py-2 text-xs font-semibold inline-flex items-center gap-2" data-testid="admin-add-marquee">
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      <div className="space-y-3">
        {words.map((w, i) => {
          const c = toL10n(w);
          return (
            <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center p-3 bg-[color:var(--gcs-ivory)] border border-[color:var(--gcs-line)] rounded-sm">
              <div className="md:col-span-5">
                <span className="overline text-[color:var(--gcs-gold)] text-[10px]">EN</span>
                <input className="w-full mt-1 px-3 py-2 bg-[color:var(--gcs-ivory-2)] border border-[color:var(--gcs-line)] rounded-sm text-sm" value={c.en} onChange={(e) => set(i, "en", e.target.value)} data-testid={`admin-marquee-${i}-en`} />
              </div>
              <div className="md:col-span-5">
                <span className="overline text-[color:var(--gcs-gold)] text-[10px]">ES</span>
                <input className="w-full mt-1 px-3 py-2 bg-[color:var(--gcs-ivory-2)] border border-[color:var(--gcs-line)] rounded-sm text-sm" value={c.es} onChange={(e) => set(i, "es", e.target.value)} data-testid={`admin-marquee-${i}-es`} />
              </div>
              <button onClick={() => remove(i)} className="md:col-span-2 text-xs text-red-700/70 hover:text-red-700 inline-flex items-center gap-1 justify-center" data-testid={`admin-remove-marquee-${i}`}>
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function toL10n(v) {
  if (v && typeof v === "object" && ("en" in v || "es" in v)) return { en: v.en || "", es: v.es || "" };
  if (typeof v === "string") return { en: v, es: v };
  return { en: "", es: "" };
}

function L10nInput({ value, onChange, textarea, testid }) {
  const c = toL10n(value);
  const set = (k, v) => onChange({ ...c, [k]: v });
  const Cmp = textarea ? "textarea" : "input";
  const cls = `w-full px-3 py-2 bg-[color:var(--gcs-ivory)] border border-[color:var(--gcs-line)] rounded-sm text-sm ${textarea ? "min-h-[80px]" : ""}`;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <span className="overline text-[color:var(--gcs-gold)] text-[10px]">EN</span>
        <Cmp className={cls + " mt-1"} value={c.en} onChange={(e) => set("en", e.target.value)} data-testid={`${testid}-en`} />
      </div>
      <div>
        <span className="overline text-[color:var(--gcs-gold)] text-[10px]">ES</span>
        <Cmp className={cls + " mt-1"} value={c.es} onChange={(e) => set("es", e.target.value)} data-testid={`${testid}-es`} />
      </div>
    </div>
  );
}

function L10nArrayEditor({ items, onChange, testid }) {
  const arr = (items || []).map(toL10n);
  const set = (i, k, v) => {
    const next = [...arr];
    next[i] = { ...next[i], [k]: v };
    onChange(next);
  };
  const add = () => onChange([...arr, { en: "", es: "" }]);
  const remove = (i) => onChange(arr.filter((_, k) => k !== i));
  return (
    <div className="space-y-2">
      {arr.map((r, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
          <input className="md:col-span-5 px-2 py-1.5 bg-[color:var(--gcs-ivory)] border border-[color:var(--gcs-line)] rounded-sm text-sm" placeholder="EN" value={r.en} onChange={(e) => set(i, "en", e.target.value)} data-testid={`${testid}-${i}-en`} />
          <input className="md:col-span-5 px-2 py-1.5 bg-[color:var(--gcs-ivory)] border border-[color:var(--gcs-line)] rounded-sm text-sm" placeholder="ES" value={r.es} onChange={(e) => set(i, "es", e.target.value)} data-testid={`${testid}-${i}-es`} />
          <button onClick={() => remove(i)} className="md:col-span-2 text-xs text-red-700/70 hover:text-red-700 inline-flex items-center gap-1 justify-center" data-testid={`${testid}-${i}-remove`}>
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        </div>
      ))}
      <button onClick={add} className="rounded-full btn-outline-emerald px-3 py-1 text-[11px] font-semibold inline-flex items-center gap-1" data-testid={`${testid}-add`}>
        <Plus className="w-3 h-3" /> Add row
      </button>
    </div>
  );
}

function ListEditor({ title, items = [], onChange, schema, emptyItem, testid }) {
  const add = () => onChange([...(items || []), { id: crypto.randomUUID(), ...emptyItem }]);
  const remove = (i) => onChange(items.filter((_, k) => k !== i));
  const update = (i, patch) => onChange(items.map((it, k) => (k === i ? { ...it, ...patch } : it)));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-[color:var(--gcs-emerald)]">{title}</h2>
        <button
          onClick={add}
          className="rounded-full btn-outline-emerald px-4 py-2 text-xs font-semibold inline-flex items-center gap-2"
          data-testid={`admin-add-${testid}`}
        >
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      <div className="space-y-4">
        {items.map((it, i) => (
          <div key={it.id || i} className="p-5 bg-[color:var(--gcs-ivory)] border border-[color:var(--gcs-line)] rounded-sm">
            <div className="flex items-center justify-between mb-3">
              <span className="overline text-[color:var(--gcs-gold)]">#{i + 1}</span>
              <button
                onClick={() => remove(i)}
                className="text-xs text-red-700/70 hover:text-red-700 inline-flex items-center gap-1"
                data-testid={`admin-remove-${testid}-${i}`}
              >
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </div>
            {schema.map((f) => {
              const v = it[f.key];
              const testId = `admin-${testid}-${i}-${f.key}`;
              if (f.uploader) return (
                <Field key={f.key} label={f.label}>
                  <ImageField url={v} onChange={(nv) => update(i, { [f.key]: nv })} uploader={f.uploader} testid={testId} />
                </Field>
              );
              if (f.l10n) return (
                <Field key={f.key} label={f.label}>
                  <L10nInput value={v} onChange={(nv) => update(i, { [f.key]: nv })} textarea={f.textarea} testid={testId} />
                </Field>
              );
              if (f.l10nArray) return (
                <Field key={f.key} label={f.label}>
                  <L10nArrayEditor items={v} onChange={(nv) => update(i, { [f.key]: nv })} testid={testId} />
                </Field>
              );
              if (f.array) return (
                <Field key={f.key} label={f.label}>
                  <TextArea
                    value={(v || []).join("\n")}
                    onChange={(text) => update(i, { [f.key]: text.split("\n").map((s) => s.trim()).filter(Boolean) })}
                    testid={testId}
                  />
                </Field>
              );
              if (f.textarea) return (
                <Field key={f.key} label={f.label}>
                  <TextArea value={v} onChange={(nv) => update(i, { [f.key]: nv })} testid={testId} />
                </Field>
              );
              if (f.boolean) return (
                <Field key={f.key} label={f.label}>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!v}
                      onChange={(e) => update(i, { [f.key]: e.target.checked })}
                      data-testid={testId}
                    />
                    {v ? "Yes" : "No"}
                  </label>
                </Field>
              );
              return (
                <Field key={f.key} label={f.label}>
                  <TextInput value={v} onChange={(nv) => update(i, { [f.key]: nv })} testid={testId} />
                </Field>
              );
            })}
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-sm text-[color:var(--gcs-emerald)]/60 italic">No items yet. Click Add to create one.</p>
        )}
      </div>
    </div>
  );
}

function ContactsList({ contacts, onReload }) {
  const del = async (id) => {
    try {
      await api.delete(`/admin/contacts/${id}`);
      toast.success("Solicitud eliminada.");
      onReload();
    } catch { toast.error("Error al eliminar."); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-[color:var(--gcs-emerald)]">Solicitudes de contacto</h2>
        <button
          onClick={onReload}
          className="rounded-full btn-outline-emerald px-4 py-2 text-xs font-semibold inline-flex items-center gap-2"
          data-testid="admin-reload-contacts"
        >
          <RefreshCw className="w-3 h-3" /> Recargar
        </button>
      </div>
      {(contacts || []).length === 0 && (
        <p className="text-sm text-[color:var(--gcs-emerald)]/60 italic">Aún no hay solicitudes.</p>
      )}
      <div className="space-y-3">
        {(contacts || []).map((c) => (
          <div key={c.id} className="p-5 bg-[color:var(--gcs-ivory)] border border-[color:var(--gcs-line)] rounded-sm" data-testid={`admin-contact-${c.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-serif text-lg text-[color:var(--gcs-emerald)]">{c.name}</div>
                <div className="text-sm text-[color:var(--gcs-emerald)]/70">{c.phone} · {c.email || "—"}</div>
                <div className="overline text-[color:var(--gcs-gold)] mt-1">{c.service_type} · {c.frequency}</div>
              </div>
              <button
                onClick={() => del(c.id)}
                className="text-xs text-red-700/70 hover:text-red-700 inline-flex items-center gap-1"
                data-testid={`admin-delete-contact-${c.id}`}
              >
                <Trash2 className="w-3 h-3" /> Eliminar
              </button>
            </div>
            {c.notes && <p className="mt-3 text-sm text-[color:var(--gcs-emerald)]/80">{c.notes}</p>}
            <div className="mt-3 text-xs text-[color:var(--gcs-emerald)]/50">
              {c.address} · {c.city} · {c.bedrooms}hab · {c.bathrooms}baños · {c.sqft || "—"}sqft · {c.created_at?.slice(0, 16).replace("T", " ")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
